import { useCallback, useMemo } from 'react'

import type { MutationRequestOptions } from '../utils/axiosConfig'
import type { AsyncAction } from '../utils/singleFlight'
import { useSingleFlightAction } from './useSingleFlightAction'

type DedupeKeyFactory<Args extends unknown[]> =
    | string
    | ((...args: Args) => string | undefined)

export interface UseMutationActionOptions<Args extends unknown[]> {
    dedupeKey?: DedupeKeyFactory<Args>
    idempotencyScope: string
    preventDuplicate?: boolean
}

export type MutationAction<Args extends unknown[], Result> = AsyncAction<
    [MutationRequestOptions, ...Args],
    Result
>

const createOperationKey = (scope: string): string => {
    const randomId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`

    return `${scope}-${randomId}`
}

export const useMutationAction = <Args extends unknown[], Result>(
    action: MutationAction<Args, Result>,
    options: UseMutationActionOptions<Args>
) => {
    const idempotencyScope = options.idempotencyScope
    const preventDuplicate = options.preventDuplicate
    const optionDedupeKey = options.dedupeKey

    const guardedAction = useCallback(
        (...args: Args): Promise<Result> | Result => {
            const dedupeKey =
                typeof optionDedupeKey === 'function'
                    ? optionDedupeKey(...args)
                    : optionDedupeKey

            return action(
                {
                    dedupeKey,
                    idempotencyKey: createOperationKey(idempotencyScope),
                    preventDuplicate: preventDuplicate ?? true,
                },
                ...args
            )
        },
        [action, optionDedupeKey, idempotencyScope, preventDuplicate]
    )

    const singleFlight = useSingleFlightAction(guardedAction)

    return useMemo(
        () => ({
            run: singleFlight.run,
            running: singleFlight.running,
        }),
        [singleFlight.run, singleFlight.running]
    )
}
